import { useEffect, useMemo, useState } from 'react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AppointmentStatusComponents, AppointmentStatusHooks } from '@frontend/appointment-status';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert, formatDate } from '@frontend/design-system';
import { useGetDataSourcesForLocation } from '../../../hooks';
import { useAppointmentEventCardShallowStore } from '../../../stores/use-appointment-event-card-store';
import { useAppointmentStatusShallowStore } from '../../schedule-calendar-components/appointment-event-card/appointmentStatusStore';
import { AppointmentStatusChip } from './AppointmentStatusChip';
import { getIntegratedAppointmentStatusList, getLatestUpdatedAppointmentStatus } from './helpers';
import {
  AppointmentStatusV3Enum,
  APPOINTMENT_STATUS_MAPPING_V3,
  AppointmentStatusType,
  WeaveAppointmentStatus,
} from './types';

type Props = {
  locationId: string;
  appointmentId: string;
  appointmentStatus?: string;
  appointmentStartDate?: string;
  appointmentReferenceId?: string;
};

const EventCardAppointmentStatusChipForIntegrated = ({
  locationId,
  appointmentId,
  appointmentStartDate,
  appointmentReferenceId,
}: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarEvents');

  const { appointmentStatus, setAppointmentStatus, resetAppointmentStatus } = useAppointmentStatusShallowStore(
    'appointmentStatus',
    'setAppointmentStatus',
    'resetAppointmentStatus'
  );

  const { selectedGroupId } = useAppScopeStore();

  const { mutateAsync: appointmentStatusWriteback, isLoading: isLoadingAppointmentStatusWriteback } =
    SchedulerV3.Mutations.useAppointmentStatusWriteBackMutation();

  const getCalendarEventQuery = SchedulerV3.Queries.useGetCalendarEventQuery({
    request: { eventId: appointmentId, startDate: appointmentStartDate, referenceId: appointmentReferenceId },
    options: { enabled: !!appointmentId },
  });

  const { data: dataSourcesData, isLoading: isLoadingDataSources } = useGetDataSourcesForLocation({
    locationId,
    isEnabled: true,
  });

  const { data: sourceTenantsData, isLoading: isLoadingSourceTenants } = SchedulerV3.Queries.useListSourceTenantsQuery({
    request: { locationId },
    options: { enabled: !!locationId },
  });

  const { data: appointmentStatusData, isLoading: isLoadingAppointmentStatusData } =
    ScheduleQueries.useGetAppointmentStatuses(locationId);

  const isWritebackEnabled = useMemo(
    () =>
      dataSourcesData?.dataSources?.some((dataSource) =>
        dataSource.integration?.capabilities?.find(
          (capability) => capability.dataType === 'APPOINTMENT_STATUS' && capability.operation === 'LIST'
        )
      ),
    [dataSourcesData]
  );

  const appointmentStatusList = getIntegratedAppointmentStatusList({
    appointmentStatusData: appointmentStatusData,
    appointmentDetails: getCalendarEventQuery.data?.event,
    sourceTenants: sourceTenantsData?.sources,
  });

  const currentStatusObj = useMemo(() => {
    const attendeeStatus = getCalendarEventQuery.data?.event?.attendeeStatus as keyof typeof AppointmentStatusV3Enum;

    return (
      appointmentStatusList?.find((status) => status.key === attendeeStatus) ||
      APPOINTMENT_STATUS_MAPPING_V3.get(attendeeStatus as AppointmentStatusV3Enum)
    );
  }, [appointmentStatusList, getCalendarEventQuery.data]);

  const resetAppointmentStatusStore = () => {
    resetAppointmentStatus();
  };

  const statusChip = useMemo(() => {
    let chip = currentStatusObj;
    if (appointmentStatus.length) {
      const existsInStore = appointmentStatus.findLast((status: AppointmentStatusType) => status.id === appointmentId);
      if (existsInStore) {
        const canUpdate = getLatestUpdatedAppointmentStatus(existsInStore);
        if (canUpdate) {
          resetAppointmentStatusStore();
          chip = currentStatusObj;
        } else {
          chip = existsInStore;
        }
      }
    }
    return chip as AppointmentStatusType;
  }, [appointmentId, appointmentStatus, currentStatusObj]);

  const handleWriteback = async (selectedStatus: AppointmentStatusType) => {
    const { key, value, text, iconName, variant } = selectedStatus;

    const weaveStatus = WeaveAppointmentStatus[key as keyof typeof AppointmentStatusV3Enum];
    const appointmentDetails = getCalendarEventQuery.data;

    const payload = {
      locationId,
      sourceTenantId: appointmentDetails?.event?.sourceTenantId ?? '',
      appointmentExternalId: appointmentDetails?.event?.details?.pReId ?? '',
      ...(selectedGroupId && { parentLocationId: selectedGroupId }),
      ...(value && { externalStatusId: value }),
      ...(weaveStatus && { weaveStatus }),
    };

    try {
      await appointmentStatusWriteback(payload);
      setAppointmentStatus({
        key,
        text,
        iconName,
        variant,
        id: appointmentId,
        updatedAt: formatDate(dayjs()),
      });
      alert.success(t('Appointment status updated. It might take a few minutes to reflect'));
    } catch (error) {
      alert.error(t('Error updating appointment status'));
    }
  };

  useEffect(() => {
    if (appointmentStatus.length) {
      resetAppointmentStatusStore();
    }
  }, [appointmentStatus.length]);

  const isLoadingStatus =
    isLoadingDataSources ||
    getCalendarEventQuery.isLoading ||
    isLoadingSourceTenants ||
    isLoadingAppointmentStatusWriteback;

  const isLoadingMenuList = isLoadingAppointmentStatusData;

  if (!statusChip) {
    return null;
  }

  return (
    <AppointmentStatusChip
      isIntegratedOffice
      isEditable={!!isWritebackEnabled}
      isLoadingStatus={isLoadingStatus}
      isLoadingList={isLoadingMenuList}
      statusList={appointmentStatusList}
      selectedStatus={statusChip}
      onChange={handleWriteback}
    />
  );
};

export const EventCardAppointmentStatusChip = ({
  locationId,
  appointmentId,
  appointmentStartDate,
  appointmentReferenceId,
  appointmentStatus,
}: Props) => {
  const { isIntegratedOffice } = useAppointmentEventCardShallowStore('isIntegratedOffice');
  const [selectedStatus, setSelectedStatus] = useState(appointmentStatus);

  useEffect(() => {
    if (!!appointmentStatus && appointmentStatus !== selectedStatus) {
      setSelectedStatus(appointmentStatus);
    }
  }, [appointmentStatus]);

  const { handleUpdateStatus, isLoading } = AppointmentStatusHooks.useUpdateAppointmentStatusV3();

  const handleStatusChange = async (status: EventStatus) => {
    setSelectedStatus(status);
    handleUpdateStatus({
      eventId: appointmentId,
      status,
      existingStartDate: appointmentStartDate,
      existingReferenceId: appointmentReferenceId,
    });
  };

  if (isIntegratedOffice) {
    return (
      <EventCardAppointmentStatusChipForIntegrated
        locationId={locationId}
        appointmentId={appointmentId}
        appointmentStartDate={appointmentStartDate}
        appointmentReferenceId={appointmentReferenceId}
      />
    );
  }

  return (
    <AppointmentStatusComponents.EditableStatusChip
      status={selectedStatus}
      onStatusChange={handleStatusChange}
      isLoading={isLoading}
    />
  );
};
